import React from 'react'

import {
  Heading,
  MediaQueries,
  Text,
  theme,
  ThemeProps,
} from '@hhgtech/hhg-components'
import Link from 'next/link'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { StyledDotSeparator } from '../authorAndDate/index.styled'
import { TableOfContent } from '../tableOfContent'

type Props = {
  name: string
  specialty?: string
  organisation?: string
  organisationUrl?: string
  image: JSX.Element
  link: string
  description?: string
  hasDivider?: boolean
  readMore?: boolean
}

const StyledExpertCard = styled.div`
  &[data-has-divider='true'] {
    background: ${(props: ThemeProps) => props.theme.colors.neutral50};
  }

  .expert__thumbnail {
    object-fit: cover;
  }

  .card__container {
    display: grid;
    padding: 16px 0;
    background: white;
    grid-gap: 16px;
    grid-template-columns: 96px auto;

    img {
      border-radius: 100px;
    }
  }

  .expert__link {
    display: flex;
    align-items: baseline;
    color: ${(props: ThemeProps) => props.theme.colors.primaryBase};
    font-weight: ${(props: ThemeProps) => props.theme.sizes.fwBold};
    text-decoration: none;

    span {
      margin-right: 6px;
    }
  }

  a {
    text-decoration: none;
  }

  &:not(:last-child) {
    padding-bottom: 8px;
  }

  .expert__inner-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .description {
    display: -webkit-box;

    overflow: hidden;
    -webkit-box-orient: vertical;
    ${MediaQueries.mbUp} {
      -webkit-line-clamp: 2;
    }
    ${MediaQueries.mbDown} {
      -webkit-line-clamp: 4;
    }
    text-overflow: ellipsis;
  }
  .position-container {
    display: block;
    flex-wrap: nowrap;
    margin-bottom: 4px;
    .dot-seperator {
      display: inline-block;
    }
    .organisation,
    .specialty {
      display: inline;
    }
  }
`

const ExpertAuthorCard = ({
  name,
  specialty,
  organisation,
  organisationUrl,
  image,
  link,
  description,
  hasDivider = true,
  readMore = true,
}: Props) => {
  const { formatMessage: f } = useIntl()

  return (
    <StyledExpertCard data-has-divider={hasDivider}>
      <div className="card__container">
        <div className="expert_thumbnail">{image}</div>
        <div className="expert__inner-content">
          <a href={link}>
            <Heading tag="h5" style={{ marginBottom: 4 }}>
              {name}
            </Heading>
          </a>
          {specialty || organisation ? (
            <TableOfContent
              size="md"
              contents={[
                ...(specialty
                  ? [
                      <Text
                        key={'1'}
                        color={theme.colors.gray500}
                        size="p3"
                        className="specialty"
                      >
                        {specialty}
                      </Text>,
                    ]
                  : []),
                ...(organisation
                  ? [
                      <Text
                        key={'2'}
                        size="p3"
                        className="organisation"
                        color={theme.colors.gray400}
                      >
                        <a href={organisationUrl || undefined} target="_blank">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: organisation,
                            }}
                          />
                        </a>
                      </Text>,
                    ]
                  : []),
              ]}
              separator={<StyledDotSeparator className="dot-seperator" />}
              className="position-container"
            />
          ) : null}
          {description ? (
            <Text
              className="description"
              color="#737373"
              size="p3"
              style={{ marginBottom: 8, fontSize: 14 }}
            >
              <span dangerouslySetInnerHTML={{ __html: description || '' }} />
            </Text>
          ) : null}
          {readMore ? (
            <Link href={link}>
              <a className="expert__link">
                <span>{f({ id: 'articlePage.readMore' })}</span>
                <img
                  loading="lazy"
                  src="/images/expertArrow.svg"
                  style={{ width: '10px', height: '10px' }}
                />
              </a>
            </Link>
          ) : null}
        </div>
      </div>
    </StyledExpertCard>
  )
}

export { ExpertAuthorCard }
